import { apply, bump, celebrate, counter, toast } from '../core/effects';
import { m, type Messages } from '../i18n';
import { sfx } from '../audio/sfx';
import { storage } from '../core/storage';
import type { ArcadeGame, Button } from '../minigames/game';
import { registerGame, type GameId } from '../minigames/registry';
import { practice } from '../content/journal';
import { h } from './dom';
import { closePanel, isPanelOpen, openPanel, refreshPanel } from './panel';

type ArcadeKey = keyof Messages['arcade']['games'];
const factories = new Map<ArcadeKey, () => ArcadeGame>();
const ICONS: Record<ArcadeKey, string> = {
  volleyball: '🏐', tennis: '🎾', rhythm: '🎹', drawing: '🎨', racing: '🏎️', circus: '🎪', minesweeper: '🐹',
  snake: '🐛', baseball: '⚾', maze: '✨', match3: '🍓', puzzle: '🧩', blocks: '🧱',
  goldfish: '🐟', ringToss: '🎯', claw: '🧸',
  dragonBoat: '🐉', stoneStack: '🪨',
};
const DAILY_CAP = 12;

/** Register a canvas game so it can be opened from the arcade or from the world. */
export function defineGame(id: ArcadeKey, make: () => ArcadeGame): void {
  factories.set(id, make);
  registerGame(id as GameId, () => playGame(id));
}

/** Also list games that have their own panel (puzzle, blocks). */
const external = new Map<ArcadeKey, () => void>();
export function defineExternal(id: ArcadeKey, open: () => void): void {
  external.set(id, open);
  registerGame(id as GameId, open);
}

export function openArcade(): void {
  openPanel('arcade', () => {
    const t = m().arcade;
    const ids = [...factories.keys(), ...external.keys()];
    return {
      title: t.title,
      className: 'arcade',
      body: [
        h('p', { class: 'hint' }, t.hint),
        h(
          'div',
          { class: 'arcade-grid' },
          ids.map((id) =>
            h(
              'button',
              {
                class: 'arcade-card',
                onclick: () => {
                  sfx('click');
                  closePanel('arcade');
                  const ext = external.get(id);
                  if (ext) ext();
                  else playGame(id);
                },
              },
              h('span', { class: 'ico' }, ICONS[id]),
              h('b', null, t.games[id].name),
              h('small', null, `${t.best}: ${best(id)}`),
            ),
          ),
        ),
      ],
    };
  });
}

function best(id: ArcadeKey): number {
  return Number(storage.get(`best.${id}`) ?? 0);
}

let raf = 0;

const GAME_HOBBY: Partial<Record<string, string>> = {
  rhythm: 'music', drawing: 'art', volleyball: 'sport', tennis: 'sport', baseball: 'sport', racing: 'sport', dragonBoat: 'sport',
  stoneStack: 'nature', goldfish: 'nature', puzzle: 'craft', blocks: 'craft',
};

export function playGame(id: ArcadeKey): void {
  const make = factories.get(id);
  if (!make) return;
  const hobby = GAME_HOBBY[id];
  if (hobby) practice(hobby);
  const game = make();
  game.reset();
  let started = false;
  let rewarded = false;
  let last = performance.now();
  const cv = document.createElement('canvas');
  cv.className = 'arcade-cv';
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  cv.width = game.w * dpr;
  cv.height = game.h * dpr;
  const ctx = cv.getContext('2d')!;

  const toLocal = (e: PointerEvent) => {
    const r = cv.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * game.w, y: ((e.clientY - r.top) / r.height) * game.h };
  };
  let swipeStart: { x: number; y: number } | null = null;
  cv.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    cv.setPointerCapture(e.pointerId);
    const p = toLocal(e);
    swipeStart = p;
    if (!started || game.over) return restart();
    game.pointer?.('down', p.x, p.y);
  });
  cv.addEventListener('pointermove', (e) => {
    if (!started) return;
    const p = toLocal(e);
    game.pointer?.('move', p.x, p.y);
  });
  cv.addEventListener('pointerup', (e) => {
    const p = toLocal(e);
    game.pointer?.('up', p.x, p.y);
    if (swipeStart && game.swipe) {
      const dx = p.x - swipeStart.x, dy = p.y - swipeStart.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) > 18) game.swipe(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up');
    }
    swipeStart = null;
  });

  const restart = () => {
    game.reset();
    started = true;
    rewarded = false;
    sfx('pop');
  };

  const finish = () => {
    if (rewarded) return;
    rewarded = true;
    if (game.score > best(id)) storage.set(`best.${id}`, String(game.score));
    const want = game.stars(game.score);
    const room = Math.max(0, DAILY_CAP - counter(`arcade.${id}`));
    const got = Math.min(want, room);
    if (got > 0) {
      bump(`arcade.${id}`, got);
      apply({ stars: got, mood: 4 }, { float: false });
      celebrate(got >= 5);
      toast(m().arcade.earned(got));
    } else if (want > 0) toast(m().arcade.capped);
    else sfx('bad');
  };

  const press = (b: Button, down: boolean) => {
    if (down && (!started || game.over)) return restart();
    game.press?.(b, down);
  };
  const onKey = (e: KeyboardEvent) => {
    if (!isPanelOpen('game')) return;
    const map: Record<string, Button> = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down', KeyA: 'left', KeyD: 'right', KeyW: 'up', KeyS: 'down', Space: 'a', Enter: 'a', KeyJ: 'a' };
    const b = map[e.code];
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.repeat) return;
    press(b, e.type === 'keydown');
    if (e.type === 'keydown' && game.swipe && b !== 'a') game.swipe(b);
  };
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('keyup', onKey, true);

  const pad = () =>
    game.buttons.length
      ? h(
          'div',
          { class: `arcade-pad n${game.buttons.length}` },
          game.buttons.map((b) => {
            const label = { left: '◀', right: '▶', up: '▲', down: '▼', a: '●' }[b];
            const el = h('button', { class: `btn pad ${b === 'a' ? 'red' : ''}`, 'aria-label': b }, label);
            el.addEventListener('pointerdown', (e) => {
              e.preventDefault();
              el.setPointerCapture(e.pointerId);
              press(b, true);
            });
            const up = () => press(b, false);
            el.addEventListener('pointerup', up);
            el.addEventListener('pointercancel', up);
            el.addEventListener('lostpointercapture', up);
            return el;
          }),
        )
      : null;

  openPanel(
    'game',
    () => {
      const t = m().arcade;
      return {
        title: `${ICONS[id]} ${t.games[id].name}`,
        className: 'game',
        body: [h('div', { class: 'arcade-score' }, h('span', { id: 'gameScore' }, `${t.score}: 0`), h('span', null, `${t.best}: ${best(id)}`)), cv, pad(), game.extra?.() ?? null, h('p', { class: 'hint' }, t.games[id].how)],
        footer: [h('button', { class: 'btn ghost', onclick: () => (closePanel('game'), openArcade()) }, t.back)],
      };
    },
    () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
    },
  );

  cancelAnimationFrame(raf);
  let shownScore = -1;
  const loop = (now: number) => {
    if (!isPanelOpen('game')) return;
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    if (started && !game.over) game.update(dt);
    if (started && game.over) finish();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    game.draw(ctx, now / 1000);
    if (!started || game.over) {
      ctx.fillStyle = 'rgba(10,6,4,0.55)';
      ctx.fillRect(0, 0, game.w, game.h);
      ctx.font = '800 22px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffd84a';
      const t = m().arcade;
      ctx.fillText(game.over ? (game.won ? t.win : t.over) : t.tap, game.w / 2, game.h / 2 - 10);
      if (game.over) {
        ctx.font = '700 15px system-ui, sans-serif';
        ctx.fillStyle = '#fff3d0';
        ctx.fillText(`${t.score}: ${game.score}  ·  ${t.again}`, game.w / 2, game.h / 2 + 18);
      }
    }
    if (game.score !== shownScore) {
      shownScore = game.score;
      const el = document.getElementById('gameScore');
      if (el) el.textContent = `${m().arcade.score}: ${game.score}`;
    }
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
  void refreshPanel;
}
